import type { Table } from "common/data/table";
import type { FeetSquared } from "common/types/nominalNumbersTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { RoomHandle } from "../handles/docEntityHandleTypes";
import type { RoomType } from "./roomTypesEnum";

export enum FloorAreaTakeoffType {
  TOTAL_FLOOR_AREA = "TOTAL_FLOOR_AREA",
  CONDITIONED_FLOOR_AREA = "CONDITIONED_FLOOR_AREA",
  UNCONDITIONED_FLOOR_AREA = "UNCONDITIONED_FLOOR_AREA",
  TOTAL_GARAGE_AREA = "TOTAL_GARAGE_AREA",
  FRONT_PORCH_AREA = "FRONT_PORCH_AREA",
  REAR_PORCH_AREA = "REAR_PORCH_AREA",
  COVERED_PATIO_AREA = "COVERED_PATIO_AREA",
  UNCOVERED_PATIO_AREA = "UNCOVERED_PATIO_AREA",
}
export enum BorderPosition {
  CENTERLINE = "CENTERLINE",
  /** inside is towards rooms in rule */
  FRAMING_INSIDE = "FRAMING_INSIDE",
  /** outside is outwards from rooms in rule */
  FRAMING_OUTSIDE = "FRAMING_OUTSIDE",
}

export enum FloorFilter {
  FIRST_FLOOR_ONLY = "FIRST_FLOOR_ONLY",
  UPPER_FLOORS_ONLY = "UPPER_FLOORS_ONLY",
}

export enum RoomSelectorType {
  UNCONDITIONED = "UNCONDITIONED",
  CONDITIONED = "CONDITIONED",
  NOT_CONDITIONED_OR_UNCONDITIONED = "NOT_CONDITIONED_OR_UNCONDITIONED",
}

export enum RoomEnclosureSimple {
  UNCONDITIONED = "UNCONDITIONED",
  CONDITIONED = "CONDITIONED",
  NONE = "NONE",
}

export type RoomBorderPosition = RoomBorderPositionByRoomType | RoomBorderPositionByRoomSelector;

export interface RoomBorderPositionByRoomType {
  readonly type: AreaRuleType.BY_ROOM_TYPE;
  readonly roomTypes: { [key in RoomType]?: boolean };
  readonly borderPosition: BorderPosition;
}
export interface RoomBorderPositionByRoomSelector {
  readonly type: AreaRuleType.BY_ROOM_SELECTOR;
  readonly roomSelectors: RoomSelectorType[];
  readonly borderPosition: BorderPosition;
}

export type RoomBorderPositions = RoomBorderPosition[];
export type AreaRule = AreaRuleByRoomType | AreaRuleByRoomSelector;
export enum AreaRuleType {
  BY_ROOM_TYPE = "BY_ROOM_TYPE",
  BY_ROOM_SELECTOR = "BY_ROOM_SELECTOR",
}
export interface AreaRuleByRoomType extends BaseAreaRule {
  readonly type: AreaRuleType.BY_ROOM_TYPE;
  readonly roomTypes: { [key in RoomType]?: boolean };
}

export interface AreaRuleByRoomSelector extends BaseAreaRule {
  readonly type: AreaRuleType.BY_ROOM_SELECTOR;
  readonly roomSelectors: RoomSelectorType[];
}

interface BaseAreaRule {
  /** will include any rooms in any open room set containing a room type in the rule */
  readonly includeAnyInSameRoomSets: boolean;
  readonly otherRoomBorderPosition?: BorderPosition;
  readonly exteriorRoomBorderPosition?: BorderPosition;
  readonly specificRoomBorderPositions?: RoomBorderPositions;
  readonly appliesOnlyTo?: FloorFilter;
}

export interface FloorAreaTakeoffConfig {
  /** an empty array will include all room types, except excluded room types. is a whitelist if not empty. */
  readonly include: AreaRule[];

  /** an empty array won't exclude any roomTypes */
  readonly exclude: AreaRule[];

  /** add areas outside of the room's pipeline */
  readonly includeSupplementalAreas?: { [key in SupplementalArea]?: boolean };
}

export enum SupplementalArea {
  FRONT_PORCH_COLONNADE = "FRONT_PORCH_COLONNADE",
  REAR_PORCH_COLONNADE = "REAR_PORCH_COLONNADE",
}

export interface LevelFloorAreaTakeoff {
  readonly floorArea: FeetSquared;
  readonly floorAreaExact: FeetSquared;
  readonly floorAreaPolygons: PolyLine2[][];
  readonly roomHandles: RoomHandle[];
}

export interface FloorAreaTakeOff {
  readonly totalFloorArea: FeetSquared;
  readonly totalFloorAreaExact: FeetSquared;
  readonly levelFloorAreas: Table<LevelFloorAreaTakeoff>; // levelId => takeoffs
}

export type FloorAreaTakeoffs = { [key in FloorAreaTakeoffType]?: FloorAreaTakeOff };

export type FloorAreaTakeoffConfigs = { [key in FloorAreaTakeoffType]?: FloorAreaTakeoffConfig };
